/**
 * Centralized accessor for the two pieces of state that used to live
 * directly in `localStorage`: the decrypted RSA private key (`chatKey`,
 * used to unwrap E2E-encrypted direct messages) and the `userID` it
 * belongs to.
 *
 * These now live in `sessionStorage` instead of `localStorage`:
 * - `localStorage` persists indefinitely and is shared across every tab
 *   for the origin, so a stolen/XSS-read value stays valid long after the
 *   user closes the browser.
 * - `sessionStorage` is cleared when the tab closes and is not shared
 *   across tabs, shrinking the window an attacker has to make use of a
 *   leaked key, while still surviving a same-tab page refresh so users
 *   aren't reprompted for their password on every reload.
 *
 * The existing "enter your password to unlock messages" flows
 * (EnterPasswordModal.js, MessageModal.js) already re-derive this value
 * from the user's password whenever it's missing, so a new tab or a
 * cleared session simply re-triggers that same prompt.
 *
 * Kept as a single module so the backing store can be changed again later
 * (e.g. to an in-memory-only store) without touching every call site.
 */

const CHAT_KEY = "chatKey";
const USER_ID_KEY = "userID";

const getChatKey = () => sessionStorage.getItem(CHAT_KEY);
const setChatKey = (value) => sessionStorage.setItem(CHAT_KEY, value);

const getUserID = () => sessionStorage.getItem(USER_ID_KEY);
const setUserID = (value) => sessionStorage.setItem(USER_ID_KEY, value);

/**
 * Clears both the new sessionStorage-backed values and the legacy
 * localStorage keys, so any key persisted before this change is also
 * cleaned up the next time a user logs out.
 */
const clear = () => {
  sessionStorage.removeItem(CHAT_KEY);
  sessionStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(CHAT_KEY);
  localStorage.removeItem(USER_ID_KEY);
};

const keyStore = {
  getChatKey,
  setChatKey,
  getUserID,
  setUserID,
  clear,
};

export default keyStore;
